// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Component Imports
import { Box, Typography, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Third-Party Imports
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

// ** Logic Imports
import { GetInvoice } from "./GetInvoice";
import { SendInvoice } from "./SendInvoice";
import { convertESTToLocal } from "components/formatDate";
import { formatUSCurrency } from "components/CurrencyFormat";
import { useInvoicePermission } from "../permissions/InvoicePermissions";

export const useIndexLogic = () => {
  //#region Permissions
  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSavePermission,
    hasInvoiceGetPermission,
    hasInvoiceDeletePermission,
    hasInvoiceSendEstEmailPermission,
    hasInvoiceSendEmailPermission,
  } = InvoicePermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const theme = useTheme();
  //#endregion

  //#region Constants
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState("");
  const [openNewWorkOrderDlg, setOpenNewWorkOrderDlg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  const [openScheduleAst, setOpenScheduleAst] = useState(false);
  const [readyToSendInvoices, setReadyToSendInvoices] = useState([]);
  const [filteredReadyToSendInv, setFilteredReadyToSendInv] = useState([]);
  const [selectedButton, setSelectedButton] = useState(0);
  const [successfulInvoices, setSuccessfulInvoices] = useState([]);
  const [failedInvoices, setFailedInvoices] = useState([]);
  const [openBatchSendDlg, setOpenBatchSendDlg] = useState(false);

  const filterButtons = [{ name: "All Invoices" }, { name: "Ready To Send" }];
  const tagManagementData = { title: "Tag Management" };

  const customerID = 0;
  const requestID = 0;

  const columns = [
    {
      // flex: 0.1,
      field: "id",
      hide: true,
      minWidth: 80,
      headerName: "Invoice ID",
      renderCell: ({ row }) => {
        const { invoiceID, id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/invoices/detail/${invoiceID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "customerName",
      minWidth: 120,
      headerName: "Customer",
      renderCell: ({ row }) => {
        const { customerName, customerID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/customers/detail/${customerID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createDate",
      minWidth: 80,
      headerName: "Created On",
      renderCell: ({ row }) => {
        const { createDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {convertESTToLocal(createDate)}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createdBy",
      headerName: "Created By",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { createdBy } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {createdBy.trim() ? createdBy : "System Generated"}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "dueDate",
      headerName: "Payment Due Date",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { dueDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {dueDate ? convertESTToLocal(dueDate) : 'N/A'}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "subTotal",
      headerName: "Invoice Total ($)",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { subTotal } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography
              sx={{
                flexGrow: 1,
                color: subTotal < 0 ? "error.main" : "inherit",
              }}
            >
              {formatUSCurrency(subTotal)}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "paymentReceived",
      headerName: "Received ($)",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { amountPaid } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography
              sx={{
                flexGrow: 1,
                color: amountPaid < 0 ? "error.main" : "inherit",
              }}
            >
              {formatUSCurrency(amountPaid)}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "paymentRemaining",
      headerName: "Remaining ($)",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { balanceDue } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography
              sx={{
                flexGrow: 1,
                color: balanceDue < 0 ? "error.main" : "inherit",
              }}
            >
              {formatUSCurrency(balanceDue)}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 80,
      renderCell: ({ row }) => {
        const { status } = row;

        // Partially Paid (Teal)

        // Sent (Violet)

        // Missed Send Date (yellow)

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Paid"
                ? "success"
                : status === "Partially Paid"
                ? "teal"
                : status === "Sent"
                ? "violet"
                : status === "Ready to Send"
                ? "info"
                : status === "Payment Past Due"
                ? "error"
                : status === "Missed Send Date"
                ? "yellow"
                : status === "Canceled"
                ? "warning"
                : "secondary"
            }
            label={status}
          />
        );
      },
    },
    {
      // flex: 0.1,
      field: "modifiedDate",
      headerName: "Last Updated",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { modifiedDate } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>
              {modifiedDate ? convertESTToLocal(modifiedDate) : ""}
            </Typography>
          </Box>
        );
      },
    },
  ];

  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    if (hasInvoiceGetPermission) {
      fetchInvoices();
    } else {
      setLoading(false);
    }
  }, [hasInvoiceGetPermission]);

  useEffect(() => {
    if (hasInvoiceGetPermission) {
      // if(selectedButton === 1){
      //   setFilteredReadyToSendInv(() =>
      //     readyToSendInvoices.filter((row) =>
      //       Object.values(row).some(
      //         (value) =>
      //           value &&
      //           value.toString().toLowerCase().includes(searchText.toLowerCase())
      //       )
      //     )
      //   );
      // }else{
      setFilteredRows(() =>
        data.filter((row) =>
          Object.values(row).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchText.toLowerCase())
          )
        )
      );
      // }
    } else {
      return;
    }
  }, [searchText]);

  useEffect(() => {
    // const total = selectedButton === 1 ? readyToSendInvoices.length : filteredRows.length;
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min(
      (paginationModel.page + 1) * paginationModel.pageSize,
      total
    );

    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }

    setPaginationText(`${end} of ${total}`);
  }, [
    filteredRows,
    paginationModel.page,
    paginationModel.pageSize,
    selectedButton,
  ]);

  const fetchInvoices = async () => {
    if (!hasInvoiceGetPermission) return;

    try {
      setLoading(true);
      let invoices = await GetInvoice(0, 0, 50, 1, navigate);
      const modifiedInvoices = invoices.map(({ invoiceID, ...rest }) => ({
        id: "INV-" + invoiceID.toString().padStart(3, "0"),
        invoiceID: invoiceID,
        ...rest,
      }));
      setData(modifiedInvoices);
      setFilteredRows(modifiedInvoices);
      // setReadyToSendInvoices(modifiedInvoices.filter((inv) => inv.status === 'Ready to Send' || inv.status === 'Overdue'));
      // setFilteredReadyToSendInv(modifiedInvoices.filter((inv) => inv.status === 'Ready to Send' || inv.status === 'Overdue'));
    } catch (error) {
      console.error("Error fetching Requests data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (index) => {
    setSelectedButton(index);
    setRowSelectionModel([]);
    setSelectedRows(0);
    setSearchText("");
  };

  const handleOpenScheduleAstDlg = () => {
    setOpenScheduleAst(true);
  };

  const handleCloseScheduleAstDlg = () => {
    setOpenScheduleAst(false);
  };

  const handleOpenNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(true);
  };
  const handleCloseNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(false);
  };

  const handleOpenTagManagementDlg = () => setOpenTagManagementDlg(true);
  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => {
    setSelectedRows(selectedRows);
  };

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText("");

  const handleOpenBatchSendDlg = () => setOpenBatchSendDlg(true);
  const handleCloseBatchSendDlg = () => setOpenBatchSendDlg(false);

  const confirmBatchSend = () => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: `
            <div>You are about to send ${selectedRows.length} invoice${selectedRows.length > 1 ? 's' : ''} to your Customer${selectedRows.length > 1 ? "s'" : "'s"} primary email address${selectedRows.length > 1 ? "es" : ""}.</div><br />
            <div>Please ensure all information is accurate before continuing.</div>
      `,
      showCancelButton: true,
      confirmButtonText: `Send ${selectedRows.length} Invoice${selectedRows.length > 1 ? 's' : ''}`,
      cancelButtonText: "Cancel",
      allowEscapeKey: true,
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup:
          theme.palette.mode === "dark"
            ? "custom-swal-popup-dark"
            : "custom-swal-popup",
        title:
          theme.palette.mode === "dark"
            ? "custom-swal-title-dark"
            : "custom-swal-title",
        htmlContainer: "custom-swal-html",
        confirmButton: "custom-swal-confirm",
        cancelButton:
          theme.palette.mode === "dark"
            ? "custom-swal-cancel-dark"
            : "custom-swal-cancel",
        closeButton: "custom-swal-close",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleBatchSend();
      }
    });
  };

  const handleBatchSend = async () => {
    let invoiceIDs = selectedRows?.map((id) =>
      parseInt(id.replace("INV-", ""), 10)
    );

    if (invoiceIDs.length <= 0 || !hasInvoiceSendEmailPermission) return;
    setLoading(true);

    const successes = [];
    const failures = [];

    try {
      for (const id of invoiceIDs) {
        try {
          const result = await SendInvoice(id, navigate);
          if (result && !result.isError) {
            successes.push({
              id: id,
              invoiceID: `INV-${id}`,
              result: result,
            });
          } else {
            failures.push({
              id: id,
              invoiceID: `INV-${id}`,
              failureReason: result?.message || "Unknown error",
              result: result,
            });
          }
        } catch (error) {
          failures.push({
            id: id,
            invoiceID: `INV-${id}`,
            failureReason: error.message || "Network error",
          });
        }
      }
      
      setSuccessfulInvoices(successes);
      setFailedInvoices(failures);
      handleOpenBatchSendDlg();
      fetchInvoices();

    } catch (error) {
      console.error("Unexpected error in batch send:", error);
      alert("An unexpected error occurred during batch send");
    } finally {
      setLoading(false);
    }
  };

  //#endregion end of handlers and methods

  const IndexProps = {
    columns,
    contentHeightFixed,
    handleOpenNewWorkOrderDlg,
    handleCloseNewWorkOrderDlg,
    schedulingReq,
    setSchedulingReq,
    openScheduleAst,
    setOpenScheduleAst,
    handleOpenScheduleAstDlg,
    handleCloseScheduleAstDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    setFilteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    openNewWorkOrderDlg,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    filteredReadyToSendInv,
    selectedButton,
    filterButtons,
    handleFilterChange,
    confirmBatchSend,
    openBatchSendDlg,
    handleCloseBatchSendDlg,
    successfulInvoices,
    failedInvoices
  };

  return {
    IndexProps,
  };
};
